import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon, 
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BellIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import {
  Users,
  BarChart3,
  Target,
  Eye,
  LayoutGrid,
  Sparkles,
  ShieldCheck,
  FileText,
  Scale,
  Trophy,
  Activity,
  Settings,
  LogOut,
  Home,
  CircleHelp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PlayerSearch } from './PlayerSearch';
import { PREFERENCE_EVENT, SIDEBAR_COLLAPSED_KEY, readPreference, writePreference } from '../utils/preferences';

interface SearchResult {
  id: number;
  name: string;
  team: string;
  position: string;
  games: number;
  url: string;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  /** A paid feature the club's plan must include (clubs/<subdomain>.yaml) */
  feature?: string;
}

const navigationSections: NavigationSection[] = [
  {
    title: 'Team management',
    items: [
      { name: 'My teams', href: '/teams', icon: Users },
      { name: 'Team management', href: '/list', icon: LayoutGrid },
      { name: 'Player stats', href: '/players', icon: BarChart3 },
      { name: 'Talent pipeline', href: '/talent-pipeline', icon: Target },
      { name: 'My watch list', href: '/watchlist', icon: Eye },
      { name: 'Scouting reports', href: '/scouting-reports', icon: FileText },
      { name: 'Player comparison', href: '/player-comparison', icon: Scale },
    ],
  },
  {
    title: 'Assistant',
    items: [
      { name: 'Ask the data', href: '/ask', icon: Sparkles, feature: 'ask_the_data' },
    ],
  },
  {
    title: 'Game reports',
    items: [
      { name: 'Game Day Reports', href: '/game-reports', icon: Trophy },
    ],
  },
  {
    title: 'Gameday analysis',
    items: [
      // The page lists every competition the user can access
      { name: 'Match centre', href: '/gameday-analysis', icon: Activity },
    ],
  },
];

interface LayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, pageTitle }) => {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  // Remembered per browser; also set from the Settings page
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readPreference(SIDEBAR_COLLAPSED_KEY, false));
  useEffect(() => {
    writePreference(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed);
  }, [sidebarCollapsed]);
  useEffect(() => {
    const onChange = (event: Event) => {
      const { key, value } = (event as CustomEvent<{ key: string; value: unknown }>).detail;
      if (key === SIDEBAR_COLLAPSED_KEY) setSidebarCollapsed(Boolean(value));
    };
    window.addEventListener(PREFERENCE_EVENT, onChange);
    return () => window.removeEventListener(PREFERENCE_EVENT, onChange);
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Auto-collapse sidebar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check on mount
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePlayerSelect = (player: SearchResult) => {
    // Navigate to player page
    // Navigate to player detail page
    navigate(player.url);
    setMobileSearchOpen(false);
  };

  // Leave out what the club's plan doesn't include, and any section left empty. A session from before
  // club config existed has no features, so everything shows, as it did.
  const features = state.user?.features;
  const sections = navigationSections
    .map(section => ({ ...section, items: section.items.filter(item => !item.feature || features?.[item.feature] !== false) }))
    .filter(section => section.items.length > 0);

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'ml-16' : 'ml-64';

  return (
    <div className="min-h-screen bg-neutral-50 font-quicksand">
      {/* Modern Sidebar */}
      <nav className={`fixed inset-y-0 left-0 z-50 ${sidebarWidth} bg-rugby-primary-500 shadow-xl transition-all duration-300 ease-in-out overflow-hidden flex flex-col`}>
        {/* Sidebar Header */}
        <div className="flex-shrink-0 flex items-center justify-between h-16 px-4 border-b border-rugby-primary-400">
          <div className={`flex items-center space-x-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
            <div className="flex-shrink-0 w-8 h-8 bg-rugby-gold-500 rounded-lg flex items-center justify-center">
              <span className="text-rugby-primary-900 font-bold text-sm">RL</span>
            </div>
            {!sidebarCollapsed && (
              <div className="text-white font-bold text-xl animate-fade-in">
                RL Scouting
              </div>
            )}
          </div>
          
          {/* Collapse Toggle - Desktop Only */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex items-center justify-center w-8 h-8 text-rugby-primary-100 hover:text-white hover:bg-rugby-primary-400 rounded-md transition-colors duration-200"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="w-4 h-4" />
            ) : (
              <ChevronLeftIcon className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation Content */}
        <div className="flex-1 min-h-0 px-3 py-4 space-y-6 overflow-y-auto">
          {sections.map((section) => (
            <div key={section.title}>
              {/* Section Title */}
              {!sidebarCollapsed && (
                <div className="px-3 mb-3">
                  <h3 className="text-xs font-semibold text-rugby-primary-200 uppercase tracking-wider animate-fade-in">
                    {section.title}
                  </h3>
                </div>
              )}
              
              {/* Section Items */}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) => `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-primary-400 hover:text-white relative ${
                        isActive
                          ? 'bg-rugby-gold-500 text-rugby-primary-900 shadow-lg'
                          : 'text-rugby-primary-100'
                      }`}
                      title={sidebarCollapsed ? item.name : undefined}
                    >
                      {({ isActive }) => (
                      <>
                      {/* Active Indicator */}
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-rugby-red-500 rounded-r-full" />
                      )}
                      
                      {/* Icon */}
                      <Icon className={`flex-shrink-0 w-5 h-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'} transition-colors duration-200`} />
                      
                      {/* Label */}
                      {!sidebarCollapsed && (
                        <span className="animate-fade-in">
                          {item.name}
                        </span>
                      )}
                      
                      {/* Badge */}
                      {item.badge && !sidebarCollapsed && (
                        <span className="ml-auto bg-rugby-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full animate-bounce-subtle">
                          {item.badge}
                        </span>
                      )}
                      </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Admin Section */}
          {state.user && (
            <div className="pt-4 border-t border-rugby-primary-400">
              {!sidebarCollapsed && (
                <div className="px-3 mb-3">
                  <h3 className="text-xs font-semibold text-rugby-primary-200 uppercase tracking-wider animate-fade-in">
                    Administration
                  </h3>
                </div>
              )}
              <NavLink
                to="/settings"
                className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-primary-400 hover:text-white text-rugby-primary-100"
                title={sidebarCollapsed ? 'Settings' : undefined}
              >
                <Settings className={`flex-shrink-0 w-5 h-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'} transition-colors duration-200`} />
                {!sidebarCollapsed && (
                  <span className="animate-fade-in">Settings</span>
                )}
              </NavLink>
              <NavLink
                to="/guide"
                className={({ isActive }) => `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-primary-400 hover:text-white ${
                  isActive ? 'bg-rugby-gold-500 text-rugby-primary-900' : 'text-rugby-primary-100'
                }`}
                title={sidebarCollapsed ? 'How to use' : undefined}
              >
                <CircleHelp className={`flex-shrink-0 w-5 h-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'} transition-colors duration-200`} />
                {!sidebarCollapsed && (
                  <span className="animate-fade-in">How to use</span>
                )}
              </NavLink>
              {state.user.role === 'admin' && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) => `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-primary-400 hover:text-white ${
                    isActive ? 'bg-rugby-gold-500 text-rugby-primary-900' : 'text-rugby-primary-100'
                  }`}
                  title={sidebarCollapsed ? 'Organisation' : undefined}
                >
                  <ShieldCheck className={`flex-shrink-0 w-5 h-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'} transition-colors duration-200`} />
                  {!sidebarCollapsed && (
                    <span className="animate-fade-in">Organisation</span>
                  )}
                </NavLink>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="flex-shrink-0 border-t border-rugby-primary-400 p-4">
          <button
            onClick={logout}
            className="group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-red-600 text-rugby-primary-100 hover:text-white"
            title={sidebarCollapsed ? 'Logout' : undefined}
          >
            <LogOut className={`flex-shrink-0 w-5 h-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'} transition-colors duration-200`} />
            {!sidebarCollapsed && (
              <span className="animate-fade-in">Logout</span>
            )}
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`min-h-screen transition-all duration-300 ease-in-out ${mainMargin}`}>
        {/* Modern Header */}
        {pageTitle && (
          <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-neutral-200 z-40">
            <div className="flex items-center justify-between h-16 px-6">
              {/* Mobile Menu Button */}
              <button
                className="lg:hidden flex items-center justify-center w-10 h-10 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors duration-200"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation menu"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>

              {/* Page Title & Breadcrumb */}
              <div className="flex-1 min-w-0 mr-4">
                <nav className="hidden sm:flex" aria-label="Breadcrumb">
                  <ol className="flex items-center space-x-2 text-sm text-neutral-500">
                    <li><Link to="/" className="hover:text-rugby-primary-600 transition-colors">Home</Link></li>
                    <li className="flex items-center"><ChevronRightIcon className="w-4 h-4 mx-2" /><span className="text-neutral-900 font-medium">{pageTitle}</span></li>
                  </ol>
                </nav>
                <h1 className="text-heading-md font-semibold text-neutral-900 sm:hidden">
                  {pageTitle}
                </h1>
              </div>

              {/* Header Actions */}
              <div className="flex items-center space-x-4">
                {/* Search */}
                <div className="hidden md:block w-80">
                  <PlayerSearch
                    onPlayerSelect={handlePlayerSelect}
                    placeholder="Search players..."
                    className="w-full"
                  />
                </div>

                {/* Mobile Search Button */}
                <button
                  className="md:hidden flex items-center justify-center w-10 h-10 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors duration-200"
                  onClick={() => setMobileSearchOpen(true)}
                  aria-label="Search"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>

                {/* Notifications */}
                <button className="relative flex items-center justify-center w-10 h-10 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors duration-200">
                  <BellIcon className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-rugby-red-500 rounded-full"></span>
                </button>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-rugby-primary-500 focus:ring-offset-2 transition-all duration-200 hover:bg-neutral-100 px-3 py-2"
                  >
                    {state.user?.picture ? (
                      <img
                        className="w-8 h-8 rounded-full object-cover"
                        src={state.user.picture}
                        alt={state.user.name || 'User'}
                      />
                    ) : (
                      <UserCircleIcon className="w-8 h-8 text-neutral-400" />
                    )}
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-neutral-900">
                        {state.user?.name || 'User'}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {state.user?.email}
                      </p>
                    </div>
                  </button>

                  {/* User Dropdown */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 animate-fade-in">
                      <div className="py-1">
                        <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100">Your Profile</Link>
                        <Link to="/settings" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100">Settings</Link>
                        <Link to="/guide" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100">How to use</Link>
                        <button onClick={logout} className="w-full text-left block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100">Sign out</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Page Content */}
        <div className={`${pageTitle ? 'pt-6' : 'pt-16'} pb-8`}>
          {children}
        </div>
      </main>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="fixed inset-y-0 left-0 w-72 bg-rugby-primary-500 overflow-y-auto animate-slide-in">
            {/* Mobile Header */}
            <div className="flex-shrink-0 flex items-center justify-between h-16 px-4 border-b border-rugby-primary-400">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-rugby-gold-500 rounded-lg flex items-center justify-center">
                  <span className="text-rugby-primary-900 font-bold text-sm">RL</span>
                </div>
                <div className="text-white font-bold text-xl">RL Scouting</div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-8 h-8 text-rugby-primary-100 hover:text-white hover:bg-rugby-primary-400 rounded-md transition-colors duration-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile Navigation Content */}
            <div className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
              {sections.map((section) => (
                <div key={section.title}>
                  <div className="px-3 mb-3">
                    <h3 className="text-xs font-semibold text-rugby-primary-200 uppercase tracking-wider">
                      {section.title}
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) => `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-primary-400 hover:text-white ${
                            isActive ? 'bg-rugby-gold-500 text-rugby-primary-900' : 'text-rugby-primary-100'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Icon className="flex-shrink-0 w-5 h-5 mr-3 transition-colors duration-200" />
                          <span>{item.name}</span>
                          {item.badge && (
                            <span className="ml-auto bg-rugby-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="px-4 pb-4">
              <NavLink
                to="/guide"
                className={({ isActive }) => `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-primary-400 hover:text-white ${
                  isActive ? 'bg-rugby-gold-500 text-rugby-primary-900' : 'text-rugby-primary-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <CircleHelp className="flex-shrink-0 w-5 h-5 mr-3 transition-colors duration-200" />
                <span>How to use</span>
              </NavLink>
            </div>

            {/* Mobile Footer */}
            <div className="flex-shrink-0 border-t border-rugby-primary-400 p-4">
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-rugby-red-600 text-rugby-primary-100 hover:text-white"
              >
                <LogOut className="flex-shrink-0 w-5 h-5 mr-3 transition-colors duration-200" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setMobileSearchOpen(false)}></div>
          <div className="fixed inset-x-0 top-0 bg-white border-b border-neutral-200 animate-slide-in">
            <div className="flex items-center h-16 px-4 space-x-4">
              <div className="flex-1">
                <PlayerSearch
                  onPlayerSelect={handlePlayerSelect}
                  placeholder="Search players..."
                  className="w-full"
                />
              </div>
              <button
                onClick={() => setMobileSearchOpen(false)}
                className="flex items-center justify-center w-10 h-10 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors duration-200"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
};